import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Card, Typography, Button, Space, Tag, Select, Input, RadioGroup, Switch, DatePicker,
  Modal, Divider, Alert, Descriptions, EmptyState, Popconfirm, Row, Col, message,
} from '../../ui';
import {
  LogIn, LogOut, MapPin, ShoppingCart,
  MessageSquare, Wrench, CheckCircle2, Plus, Wallet,
  Clock,
} from 'lucide-react';
import dayjs, { Dayjs } from 'dayjs';
import { request } from '../../api/client';
import { useAuthStore } from '../../store/auth';
import { formatIst } from '../../lib/time';
import { FarmerFormModal } from '../farmers/FarmerFormModal';
import { FileUploadField } from '../../components/FileUploadField';
import { FileExpenseReportModal } from '../expense/ExpenseReportsPage';
import { MyRegularizationsCard } from './regularization';
import { MyAttendanceHistoryCard } from './MyAttendanceHistory';
import { useGeoOrManual, type Coords } from '../../hooks/useGeoOrManual';

const SENTIMENTS = [
  { value: 'POSITIVE', label: '🙂 Positive' },
  { value: 'NEUTRAL', label: '😐 Neutral' },
  { value: 'NEGATIVE', label: '🙁 Negative' },
];
const COMPLAINT_CATS = ['GENERAL', 'QUALITY', 'PACKAGING', 'PAYMENT', 'DELIVERY', 'OTHER'];
const SEVERITIES = ['LOW', 'MED', 'HIGH', 'CRIT'];
const errMsg = (e: any) => e?.response?.data?.error?.message ?? e?.message ?? 'Failed';

export function FieldJourneyPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  // The field journey is for field staff — a Sales Person OR a Sales Head (a head personally
  // works his own beat alongside oversight). Gate by role (a permission gate can't exclude
  // SUPER_ADMIN, who holds the '*' wildcard).
  const role = useAuthStore((s) => s.user?.role);
  const canJourney = role === 'SALES_PERSON' || role === 'SALES_HEAD';

  const { data: myDay, isLoading } = useQuery({
    queryKey: ['my-day'],
    queryFn: () => request<any>({ url: '/ffa/my-day' }),
    enabled: canJourney,
  });
  // The rep's wallet — running balance (+ float outstanding / − reimbursement due).
  const { data: wallet } = useQuery({
    queryKey: ['my-wallet'],
    queryFn: () => request<any>({ url: '/wallets/me' }),
    enabled: canJourney,
  });

  const attendance = myDay?.attendance ?? null;
  const openVisit = myDay?.openVisit ?? null;
  const punchedIn = !!attendance?.checkInAt && !attendance?.checkOutAt;
  const dayCompleted = !!attendance?.checkOutAt;

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['my-day'] });
    qc.invalidateQueries({ queryKey: ['my-wallet'] });
  };
  const errOf = (e: any) => e?.response?.data?.error?.message ?? e?.message ?? 'Failed';

  // Device GPS with a manual-entry fallback (desktops/servers have no GPS provider).
  const geo = useGeoOrManual();

  // ── Attendance ───────────────────────────────────────────────────────────
  const [busy, setBusy] = useState<string | null>(null);
  const withGps = async (key: string, fn: (pos: Coords) => Promise<void>) => {
    setBusy(key);
    try {
      const pos = await geo.resolve();
      if (!pos) return; // user cancelled the manual-location entry
      await fn(pos);
      refresh();
    } catch (e: any) {
      message.error(errOf(e));
    } finally {
      setBusy(null);
    }
  };

  const punchIn = () => withGps('punchin', async (pos) => {
    await request({
      url: '/attendance/check-in', method: 'POST',
      data: { lat: pos.lat, lng: pos.lng, faceScore: 0.93, isMockLocation: false, addressText: `lat ${pos.lat.toFixed(4)}, lng ${pos.lng.toFixed(4)}` },
    });
    message.success('Punched in — attendance marked PRESENT');
  });

  const punchOut = () => withGps('punchout', async (pos) => {
    await request({ url: '/attendance/check-out', method: 'POST', data: { lat: pos.lat, lng: pos.lng } });
    message.success('Punched out — day completed');
  });

  // ── Start visit (retailer / farmer) ────────────────────────────────────────
  const [visitKind, setVisitKind] = useState<'RETAILER' | 'FARMER'>('RETAILER');
  const [retailerId, setRetailerId] = useState<string | undefined>();
  const [farmerMode, setFarmerMode] = useState<'existing' | 'new'>('existing');
  const [farmerId, setFarmerId] = useState<string | undefined>();
  const [farmerModalOpen, setFarmerModalOpen] = useState(false);
  const [registeredFarmer, setRegisteredFarmer] = useState<any>(null);
  const [notes, setNotes] = useState('');

  const { data: retailers } = useQuery({
    queryKey: ['retailers-pick'],
    queryFn: () => request<any>({ url: '/retailers', params: { pageSize: 200 } }),
    enabled: canJourney,
  });
  const { data: farmers } = useQuery({
    queryKey: ['farmers-pick'],
    queryFn: () => request<any>({ url: '/farmers', params: { pageSize: 200 } }),
    enabled: canJourney,
  });
  const retailerList = useMemo(() => retailers?.data ?? retailers ?? [], [retailers]);
  const farmerList = useMemo(() => farmers?.data ?? farmers ?? [], [farmers]);

  const startVisit = () => {
    // Validate before asking for GPS so the user gets a clear, immediate message.
    const fid = visitKind === 'FARMER' ? (farmerMode === 'existing' ? farmerId : registeredFarmer?.id) : undefined;
    if (visitKind === 'RETAILER' && !retailerId) { message.error('Pick a retailer'); return; }
    if (visitKind === 'FARMER' && !fid) { message.error(farmerMode === 'existing' ? 'Pick a farmer' : 'Register the farmer first'); return; }
    withGps('startvisit', async (pos) => {
      if (visitKind === 'RETAILER') {
        await request({ url: '/visits/retailer', method: 'POST', data: { retailerId, lat: pos.lat, lng: pos.lng, notes: notes || undefined } });
      } else {
        await request({ url: '/visits/farmer', method: 'POST', data: { farmerId: fid, lat: pos.lat, lng: pos.lng, notes: notes || undefined } });
      }
      message.success('Reached — visit started');
      setRetailerId(undefined); setFarmerId(undefined); setRegisteredFarmer(null); setNotes('');
    });
  };

  // ── In-visit modals ────────────────────────────────────────────────────────
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [complaintOpen, setComplaintOpen] = useState(false);
  const [expenseOpen, setExpenseOpen] = useState(false);
  const [leaveOpen, setLeaveOpen] = useState(false);

  if (!canJourney) {
    return <Alert type="info" title="My Field Day is for field staff (Sales Persons and Sales Heads)." />;
  }

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto' }}>
      <Typography.Title level={4}>My Field Day</Typography.Title>

      {/* Hero action bar: today's status (left) + wallet & punch action (right) — fills the width. */}
      <Card className="mb-4">
        <Row align="middle" justify="space-between" gutter={[16, 16]}>
          <Col xs={24} md={14}>
            {dayCompleted ? (
              <Space align="start" size="middle">
                <CheckCircle2 size={28} style={{ color: '#1F8A4C' }} />
                <div>
                  <div style={{ fontSize: 16, fontWeight: 600 }}>Day completed</div>
                  <Typography.Text type="secondary">In {formatIst(attendance.checkInAt)} · Out {formatIst(attendance.checkOutAt)}</Typography.Text>
                </div>
              </Space>
            ) : punchedIn ? (
              <Space align="start" size="middle">
                <Clock size={28} style={{ color: '#E7B22C' }} />
                <div>
                  <div style={{ fontSize: 16, fontWeight: 600 }}>On duty</div>
                  <Typography.Text type="secondary">Since {formatIst(attendance.checkInAt)}</Typography.Text>
                  {Array.isArray(attendance.anomalyFlags) && attendance.anomalyFlags.length > 0 && (
                    <div style={{ marginTop: 4 }}><Space wrap size={4}>{attendance.anomalyFlags.map((f: string) => <Tag key={f} tone="red">{f}</Tag>)}</Space></div>
                  )}
                </div>
              </Space>
            ) : (
              <Space align="start" size="middle">
                <LogIn size={28} style={{ color: '#1F8A4C' }} />
                <div>
                  <div style={{ fontSize: 16, fontWeight: 600 }}>Start your day</div>
                  <Typography.Text type="secondary">Punch in with your current location. Retailer / farmer visits unlock after you punch in.</Typography.Text>
                </div>
              </Space>
            )}
          </Col>
          <Col xs={24} md={10}>
            <Space size="large" wrap style={{ width: '100%', justifyContent: 'flex-end' }}>
              {wallet && (
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 11, color: '#8c8c8c' }}><Wallet size={16} /> Wallet ({Number(wallet.currentBalance) >= 0 ? 'float' : 'due'})</div>
                  <b style={{ fontSize: 16, color: Number(wallet.currentBalance) > 0 ? '#1F8A4C' : Number(wallet.currentBalance) < 0 ? '#D9363E' : '#8c8c8c' }}>
                    ₹{Number(wallet.currentBalance ?? 0).toLocaleString('en-IN')}
                  </b>
                </div>
              )}
              {dayCompleted ? (
                <Button variant="primary" icon={<LogIn size={16} />} loading={busy === 'punchin'} onClick={punchIn}>Punch In Again</Button>
              ) : punchedIn ? (
                <Popconfirm
                  title="End your day?"
                  description="Ends your current session. You can punch in again later if you head back out."
                  okText="Punch out" okButtonProps={{ danger: true }} cancelText="Stay on duty"
                  disabled={!!openVisit} onConfirm={punchOut}
                >
                  <Button variant="danger" size="lg" icon={<LogOut size={16} />} loading={busy === 'punchout'} disabled={!!openVisit}>Punch Out</Button>
                </Popconfirm>
              ) : (
                <Button variant="primary" size="lg" icon={<LogIn size={16} />} loading={busy === 'punchin'} onClick={punchIn}>Punch In</Button>
              )}
            </Space>
          </Col>
        </Row>
        {punchedIn && !!openVisit && (
          <Alert style={{ marginTop: 12 }} type="warning" title="Leave your current visit before punching out." />
        )}
      </Card>

      {/* Active visit */}
      {punchedIn && openVisit && (
        <Card title={<Space><MapPin size={16} />Active visit</Space>} className="mb-4">
          <Descriptions size="small" column={1} style={{ marginBottom: 12 }}>
            <Descriptions.Item label="Visiting">
              {openVisit.party?.name ?? (openVisit.visitType === 'FARMER' ? 'Farmer' : 'Retailer')}
              {' '}<Tag tone={openVisit.visitType === 'FARMER' ? 'green' : 'blue'}>{openVisit.visitType}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Reached at">{formatIst(openVisit.checkInAt)}</Descriptions.Item>
            <Descriptions.Item label="In geofence">{openVisit.checkInWithinGeofence ? 'Yes' : 'No'}</Descriptions.Item>
          </Descriptions>
          <Space wrap size="middle" style={{ width: '100%' }}>
            {openVisit.visitType === 'RETAILER' && (
              <Button icon={<ShoppingCart size={16} />}
                onClick={() => navigate(`/orders/new?retailerId=${openVisit.retailerId}&visitId=${openVisit.id}`)}>
                New Order
              </Button>
            )}
            <Button icon={<MessageSquare size={16} />} onClick={() => setFeedbackOpen(true)}>Feedback</Button>
            <Button icon={<Wrench size={16} />} onClick={() => setComplaintOpen(true)}>Complaint</Button>
            <Button icon={<Wallet size={16} />} onClick={() => setExpenseOpen(true)}>Add expense</Button>
            <Button variant="danger" icon={<LogOut size={16} />} onClick={() => setLeaveOpen(true)}
              style={{ marginLeft: 'auto' }}>
              Leave (check out)
            </Button>
          </Space>
        </Card>
      )}

      {/* Start a visit */}
      {punchedIn && !openVisit && (
        <Card title="Start a visit">
          <RadioGroup
            value={visitKind}
            onChange={(v) => setVisitKind(v as 'RETAILER' | 'FARMER')}
            inline
            className="mb-3"
            options={[
              { value: 'RETAILER', label: 'Retailer' },
              { value: 'FARMER', label: 'Farmer' },
            ]}
          />

          {visitKind === 'RETAILER' ? (
            <Select showSearch optionFilterProp="label" style={{ width: '100%', marginBottom: 12 }}
              placeholder="Search a retailer" value={retailerId} onChange={setRetailerId}
              options={retailerList.map((r: any) => {
                // Deactivated retailers can't be visited (backend blocks it) — disable them.
                const blocked = r.status === 'SUSPENDED' || r.status === 'CLOSED';
                return {
                  value: String(r.id),
                  label: `${r.companyName ?? r.company_name} (${r.code})${blocked ? ` — ${r.status === 'CLOSED' ? 'Closed' : 'Suspended'}` : ''}`,
                  disabled: blocked,
                };
              })} />
          ) : (
            <Space direction="vertical" style={{ width: '100%', marginBottom: 12 }}>
              <RadioGroup
                value={farmerMode}
                onChange={(v) => setFarmerMode(v as 'existing' | 'new')}
                inline
                options={[
                  { value: 'existing', label: 'Existing farmer' },
                  { value: 'new', label: 'Register new' },
                ]}
              />
              {farmerMode === 'existing' ? (
                <Select showSearch optionFilterProp="label" style={{ width: '100%' }}
                  placeholder="Search a farmer" value={farmerId} onChange={setFarmerId}
                  notFoundContent={<EmptyState compact title="No farmers" />}
                  options={farmerList.map((f: any) => {
                    const blocked = f.status === 'INACTIVE';
                    return {
                      value: String(f.id),
                      label: `${f.fullName ?? f.full_name}${f.mobile ? ` · ${f.mobile}` : ''}${blocked ? ' — Inactive' : ''}`,
                      disabled: blocked,
                    };
                  })} />
              ) : registeredFarmer ? (
                <Space>
                  <Tag tone="green">New farmer: {registeredFarmer.fullName}</Tag>
                  <Button size="sm" onClick={() => { setRegisteredFarmer(null); setFarmerModalOpen(true); }}>Change</Button>
                </Space>
              ) : (
                <Button icon={<Plus size={16} />} onClick={() => setFarmerModalOpen(true)}>Register new farmer</Button>
              )}
            </Space>
          )}
          <Input.TextArea rows={2} placeholder="Notes (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} style={{ marginBottom: 12 }} />
          <Button variant="primary" icon={<MapPin size={16} />} loading={busy === 'startvisit'} onClick={startVisit}>
            Start visit
          </Button>
        </Card>
      )}

      {/* Your own attendance history (default: active days; checkbox reveals leave/absent/holiday). */}
      <div style={{ marginBottom: 16 }}><MyAttendanceHistoryCard /></div>
      {/* Forgot to punch? Request a senior to mark that day on your behalf. */}
      <div style={{ marginBottom: 16 }}><MyRegularizationsCard /></div>

      {/* Full farmer registration (same rich modal as the Farmers page) used when
          starting a visit to a brand-new farmer. */}
      <FarmerFormModal
        open={farmerModalOpen}
        onClose={() => setFarmerModalOpen(false)}
        onSaved={(f: any) => { setRegisteredFarmer(f); setFarmerModalOpen(false); }}
      />

      {expenseOpen && openVisit && (
        <FileExpenseReportModal open visitId={String(openVisit.id)} onClose={() => setExpenseOpen(false)} onCreated={refresh} />
      )}
      {leaveOpen && openVisit && (
        <LeaveModal visit={openVisit} onClose={() => setLeaveOpen(false)} onDone={refresh} resolvePosition={geo.resolve} />
      )}

      {/* Manual-location fallback shown when device GPS is unavailable. */}
      {geo.modal}
      {feedbackOpen && openVisit && (
        <FeedbackModal visit={openVisit} onClose={() => setFeedbackOpen(false)} onDone={refresh} />
      )}
      {complaintOpen && openVisit && (
        <ComplaintModal visit={openVisit} onClose={() => setComplaintOpen(false)} onDone={refresh} />
      )}
    </div>
  );
}

// ── In-visit: structured feedback ─────────────────────────────────────────────
function FeedbackModal({ visit, onClose, onDone }: { visit: any; onClose: () => void; onDone: () => void }) {
  const [sentiment, setSentiment] = useState<string | undefined>(visit.feedbackSentiment ?? undefined);
  const [remark, setRemark] = useState(visit.feedbackRemark ?? '');
  const [followUp, setFollowUp] = useState<boolean>(!!visit.followUpNeeded);
  const [followUpDate, setFollowUpDate] = useState<Dayjs | null>(visit.followUpDate ? dayjs(visit.followUpDate) : null);

  const save = useMutation({
    mutationFn: () => request({
      url: `/visits/${visit.id}/feedback`, method: 'PATCH',
      data: {
        sentiment, remark: remark || undefined,
        followUpNeeded: followUp,
        followUpDate: followUp && followUpDate ? followUpDate.format('YYYY-MM-DD') : undefined,
      },
    }),
    onSuccess: () => { message.success('Feedback saved'); onDone(); onClose(); },
    onError: (e: any) => message.error(e?.response?.data?.error?.message ?? 'Failed'),
  });

  return (
    <Modal
      open onOpenChange={(o) => { if (!o) onClose(); }} title="Visit feedback"
      footer={<>
        <Button variant="default" onClick={onClose}>Cancel</Button>
        <Button variant="primary" loading={save.isPending} onClick={() => save.mutate()}>Save</Button>
      </>}
    >
      <div style={{ marginBottom: 6 }}>Sentiment</div>
      <Select style={{ width: '100%', marginBottom: 12 }} placeholder="Select" value={sentiment} onChange={setSentiment} options={SENTIMENTS} />
      <div style={{ marginBottom: 6 }}>Remark</div>
      <Input.TextArea rows={3} value={remark} onChange={(e) => setRemark(e.target.value)} placeholder="Notes from the visit" style={{ marginBottom: 12 }} />
      <Space>
        <Switch checked={followUp} onCheckedChange={setFollowUp} /> Follow-up needed
        {followUp && <DatePicker value={followUpDate} onChange={setFollowUpDate} placeholder="Follow-up date" />}
      </Space>
    </Modal>
  );
}

// ── In-visit: raise a complaint ───────────────────────────────────────────────
function ComplaintModal({ visit, onClose, onDone }: { visit: any; onClose: () => void; onDone: () => void }) {
  const [category, setCategory] = useState('QUALITY');
  const [severity, setSeverity] = useState('MED');
  const [desc, setDesc] = useState('');

  const save = useMutation({
    mutationFn: () => request({
      url: '/complaints', method: 'POST',
      data: {
        source: 'SALES', category, severity, description: desc,
        visitId: String(visit.id),
        retailerId: visit.visitType === 'RETAILER' ? String(visit.retailerId) : undefined,
        farmerId: visit.visitType === 'FARMER' && visit.farmerId ? String(visit.farmerId) : undefined,
      },
    }),
    onSuccess: (r: any) => { message.success(`Complaint ${r?.complaintNo ?? ''} created`); onDone(); onClose(); },
    onError: (e: any) => message.error(e?.response?.data?.error?.message ?? 'Failed'),
  });

  return (
    <Modal
      open onOpenChange={(o) => { if (!o) onClose(); }} title="Raise complaint"
      footer={<>
        <Button variant="default" onClick={onClose}>Cancel</Button>
        <Button variant="primary" disabled={desc.trim().length < 10} loading={save.isPending} onClick={() => save.mutate()}>Submit</Button>
      </>}
    >
      <Space style={{ marginBottom: 12 }}>
        <Select value={category} onChange={setCategory} style={{ width: 160 }} options={COMPLAINT_CATS.map((c) => ({ value: c, label: c }))} />
        <Select value={severity} onChange={setSeverity} style={{ width: 120 }} options={SEVERITIES.map((s) => ({ value: s, label: s }))} />
      </Space>
      <Input.TextArea rows={4} value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Describe the issue (min 10 chars)" />
      <Divider style={{ margin: '12px 0 0' }} />
      <Typography.Text type="secondary" style={{ fontSize: 12 }}>Linked to this visit and the {visit.visitType.toLowerCase()}.</Typography.Text>
    </Modal>
  );
}

// Leaving is now just a confirmation — expenses are logged via the "Add expense"
// button during the visit, so check-out has a single job: stamp the leave time + GPS.
function LeaveModal({ visit, onClose, onDone, resolvePosition }: { visit: any; advance?: any; onClose: () => void; onDone: () => void; resolvePosition: () => Promise<Coords | null> }) {
  const [busy, setBusy] = useState(false);

  const leave = async () => {
    setBusy(true);
    try {
      const pos = await resolvePosition();
      if (!pos) { setBusy(false); return; } // user cancelled the manual-location entry
      await request({ url: `/visits/${visit.id}/check-out`, method: 'POST', data: { lat: pos.lat, lng: pos.lng } });
      message.success('Visit checked out');
      onDone(); onClose();
    } catch (e) { message.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <Modal
      open onOpenChange={(o) => { if (!o) onClose(); }} title="Leave this visit?"
      footer={<>
        <Button variant="default" onClick={onClose}>Cancel</Button>
        <Button variant="danger" loading={busy} onClick={leave}>Leave (check out)</Button>
      </>}
    >
      <Typography.Paragraph style={{ marginBottom: 8 }}>
        You'll be checked out of <b>{visit.party?.name ?? String(visit.visitType ?? '').toLowerCase()}</b> and your leave
        time &amp; location will be recorded.
      </Typography.Paragraph>
      <Alert type="info"
        title="Logged all your expenses for this visit?"
        description='Add any pending travel / food / fuel costs with the "Add expense" button before you leave — you can’t add them to this visit after checking out.' />
    </Modal>
  );
}
